import { useId, useState } from "react";
import { ChevronDown, ChevronUp, List } from "lucide-react";
import { warnings, type Locale, type MapDocument, type PublishedMap } from "@51time/domain";
import "./map-legend.css";

const copy = {
  en: {
    legend: "Legend", show: "Show legend", hide: "Hide legend",
    point: "Place / point", structure: "Numbered reference", gate: "Entrance / gate",
    stop: "Stop / restriction", arrow: "Direction", label: "Place label",
    "approved-road": "Good road", "wrong-road": "Do not use road", "closed-road": "Road closed",
    line: "Marked line", area: "Marked area", freehand: "Hand drawing", "reference-line": "Private reference",
    location: "Your approximate location", hint: "Tap a marking for its details.",
  },
  es: {
    legend: "Leyenda", show: "Mostrar leyenda", hide: "Ocultar leyenda",
    point: "Lugar / punto", structure: "Referencia numerada", gate: "Entrada / portón",
    stop: "Alto / restricción", arrow: "Dirección", label: "Nombre del lugar",
    "approved-road": "Vía permitida", "wrong-road": "No usar esta vía", "closed-road": "Vía cerrada",
    line: "Línea marcada", area: "Zona marcada", freehand: "Dibujo a mano", "reference-line": "Referencia privada",
    location: "Su ubicación aproximada", hint: "Toque una marca para ver sus detalles.",
  },
  fr: {
    legend: "Légende", show: "Afficher la légende", hide: "Masquer la légende",
    point: "Lieu / point", structure: "Repère numéroté", gate: "Entrée / portail",
    stop: "Arrêt / restriction", arrow: "Direction", label: "Nom du lieu",
    "approved-road": "Route autorisée", "wrong-road": "Route à ne pas emprunter", "closed-road": "Route fermée",
    line: "Ligne dessinée", area: "Zone marquée", freehand: "Dessin à la main", "reference-line": "Référence privée",
    location: "Votre position approximative", hint: "Touchez un repère pour voir les détails.",
  },
} as const;

type Props = {
  features: MapDocument["features"] | PublishedMap["features"];
  locale?: Locale;
  locating?: boolean;
};

const symbols: Record<string, string> = {
  point: "●", structure: "123", gate: "↥", stop: "✕", arrow: "➜", label: "Aa",
};

export function MapLegend({ features, locale = "en", locating = false }: Props) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const t = copy[locale];
  // The feature's explicit kind determines meaning; color alone never implies permission.
  const kinds = [...new Map(features.map((feature) => [feature.kind, feature])).values()];
  const warningKinds = [...new Set(features.map((feature) => feature.warning))]
    .filter((warning) => warning !== "none");
  if (!kinds.length && !locating) return null;
  return (
    <div className={`map-legend${open ? " is-open" : ""}`}>
      {open && (
        <div className="map-legend-panel" id={panelId} role="region" aria-label={t.legend}>
          <ul>
            {kinds.map((feature) => (
              <li key={feature.kind}>
                <span
                  className={`map-legend-symbol ${feature.geometry.type === "Polygon" ? "is-area" : feature.geometry.type === "LineString" ? "is-line" : ""} ${feature.kind === "structure" ? "is-number" : ""}`}
                  style={{ color: feature.color }}
                  aria-hidden="true"
                >
                  {symbols[feature.kind] ?? ""}
                </span>
                <span>{t[feature.kind]}</span>
              </li>
            ))}
            {warningKinds.map((warning) => (
              <li key={warning}>
                <span className="map-legend-symbol is-warning" aria-hidden="true">!</span>
                <span>{warnings[warning][locale]}</span>
              </li>
            ))}
            {locating && (
              <li><span className="map-legend-symbol is-location" aria-hidden="true">●</span><span>{t.location}</span></li>
            )}
          </ul>
          <p>{t.hint}</p>
        </div>
      )}
      <button type="button" className="map-legend-toggle" aria-expanded={open} aria-controls={panelId}
        aria-label={open ? t.hide : t.show} onClick={() => setOpen(!open)}>
        <List size={15} aria-hidden="true" />
        {t.legend}
        {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronUp size={14} aria-hidden="true" />}
      </button>
    </div>
  );
}
